import { useId } from "react";

interface Props {
  label: string;
  value: string;
  sublabel?: string;
  accent?: string;
  /** Optional sparkline series (0..1 normalised, ~14 points). If omitted, none drawn. */
  spark?: number[];
  /** Deterministic seed used to synthesise a sparkline if `spark` not supplied. */
  seed?: number;
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  // useId gives us a render-stable, collision-free ID even when two KPICards
  // share the same seed/initial-value (which would have collided in v1).
  const reactId = useId();
  if (data.length < 2) return null;
  const W = 96;
  const H = 28;
  const step = W / (data.length - 1);
  const pts = data.map((y, i) => [i * step, H - y * H] as const);
  const path = pts
    .map(([x, y], i) => (i === 0 ? "M" : "L") + x.toFixed(1) + "," + y.toFixed(1))
    .join(" ");
  const area = path + ` L ${W},${H} L 0,${H} Z`;
  const id = "sg-" + reactId.replace(/:/g, "");
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      style={{ display: "block", overflow: "visible" }}
    >
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function syntheticSeries(seed = 1) {
  // Smooth, deterministic curve seeded by index — gives each KPI its own shape.
  const N = 14;
  let prev = 0.4 + ((seed * 13) % 30) / 100;
  const ys = [prev];
  for (let i = 1; i < N; i++) {
    const drift = Math.sin((i + seed) * 0.9) * 0.05 + 0.012;
    prev = Math.max(0.05, Math.min(0.95, prev + drift));
    ys.push(prev);
  }
  return ys;
}

export default function KPICard({
  label,
  value,
  sublabel,
  accent = "#2E8BEF",
  spark,
  seed,
}: Props) {
  const series = spark ?? syntheticSeries(seed ?? 1);
  return (
    <div className="panel lift p-5 relative overflow-hidden kpi">
      <div
        className="absolute inset-x-0 top-0"
        style={{
          height: 2,
          background: `linear-gradient(90deg, ${accent}, ${accent}33)`,
          color: accent,
        }}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-2xs uppercase tracking-[.08em] text-mut-300 font-semibold">
            {label}
          </div>
          <div
            className="text-3xl font-semibold mt-1.5 leading-none"
            style={{ letterSpacing: "-.025em" }}
          >
            {value}
          </div>
          {sublabel && <div className="text-sm text-mut-300 mt-1.5">{sublabel}</div>}
        </div>
        <div style={{ color: accent, opacity: 0.9 }}>
          <Sparkline data={series} color={accent} />
        </div>
      </div>
    </div>
  );
}
